import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Modal, useToast } from '@kss/ui';
import {
  useAuth,
  decodeJwt,
  extractPermissions,
  isPanelRole,
} from '@kss/auth';
import { authService } from '@kss/api';
import { PasswordField } from './PasswordField';
import { getTenantFromHost, shouldShowCompanyNameInput } from '../utils/appSettings';

// Inline validators (panel has its own formatValidators module; the web
// shell needs one helper each — too small to justify a shared package).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function validateEmail(v: string): string | null {
  if (!v) return 'Email is required.';
  return EMAIL_RE.test(v) ? null : 'Invalid email address.';
}
function validateMobile(v: string): string | null {
  // Allow with or without +91 / leading 0; ten-digit Indian mobile.
  const digits = v.replace(/[^0-9]/g, '').replace(/^91|^0/, '');
  return /^[6-9]\d{9}$/.test(digits) ? null : 'Invalid mobile number.';
}

export interface RegisterModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided, navigate here after sign-in instead of the role-based default. */
  forceRedirect?: string;
  /**
   * Switch back to the sign-in modal. The host listens for the
   * `kss:open-login` event after closing this modal.
   */
  onSwitchToLogin?: () => void;
}

/**
 * Inline sign-up modal for the public web. POSTs to the live
 * `/api/auth/register` endpoint (verified 2026-05-05) and auto-logs
 * the user in — the response is the same `AuthTokenBundle` that
 * `/auth/Login` returns, so the SPA can hydrate the session
 * immediately and skip a follow-up login round-trip.
 *
 * Tenant resolution priority:
 *   1. Manually-typed company name (defaults to the hostname-derived
 *      tenant, falling back to `sso` for localhost dev).
 *   2. Server-side fallback: backend resolves by Host header / domain.
 */
export function RegisterModal({
  open,
  onClose,
  forceRedirect,
  onSwitchToLogin,
}: RegisterModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();

  /* Default tenant code is auto-derived from the hostname's first
     label (`sso.kristysoftware.com` → `sso`). Users on a tenant-owned
     subdomain never have to type it. Set `VITE_SHOW_COMPANY_NAME=Y`
     to expose the override textbox for white-label resellers. Bug
     ref: AI Agent 2026-05-26 — "company name should be sub domain
     or domain name and hide by default". */
  const [companyName, setCompanyName] = useState(() => getTenantFromHost());
  const showCompanyInput = shouldShowCompanyNameInput();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setError(null);
    setPassword('');
    setConfirm('');
    setSubmitting(false);
  }

  /**
   * Client-side validation that mirrors the live API's contract
   * (verified 2026-05-05): firstName ≥ 1 char, email valid, password
   * ≥ 6 chars, plus the obvious "passwords match" check.
   */
  function localValidate(): string | null {
    /* Only block on a blank company when the textbox is actually
       shown — when hidden, the host-derived tenant code is the
       authoritative source and is always populated. */
    if (showCompanyInput && !companyName.trim()) return 'Company name is required.';
    if (!firstName.trim()) return 'First name is required.';
    if (!userName.trim()) return 'Username is required.';
    if (!email.trim()) return 'Email is required.';
    const emailErr = validateEmail(email.trim());
    if (emailErr) return emailErr;
    if (phone && validateMobile(phone)) {
      return 'Phone must be a 10-digit Indian mobile number.';
    }
    if (!password) return 'Password is required.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    if (password !== confirm) return 'Passwords do not match.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const localErr = localValidate();
    if (localErr) {
      setError(localErr);
      return;
    }
    setSubmitting(true);
    try {
      const resp = await authService.register({
        tenantCode: companyName.trim(),
        userName: userName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
      });
      // The live `/auth/register` returns the same flat token bundle
      // as `/auth/Login`, so we can sign the user in immediately.
      const claims = decodeJwt(resp.accessToken);
      const role = String(
        claims?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
          claims?.role ??
          '',
      );
      const displayName = String(claims?.name ?? resp.userName);
      const claimEmail = String(claims?.email ?? email.trim());
      const permissions = extractPermissions(claims);
      login(
        {
          id: resp.userId,
          name: displayName,
          email: claimEmail,
          role,
          permissions,
          tenantId: resp.tenantId,
        },
        {
          accessToken: resp.accessToken,
          refreshToken: resp.refreshToken,
          expiresAt: resp.expiresAt,
          tenantId: resp.tenantId,
          sessionId: resp.sessionId,
        },
      );
      toast.success('Account created', `Welcome, ${displayName}`);
      reset();
      onClose();
      /* After registration, panel-role users land on the panel
         dashboard; everyone else (the default "customer" role) lands
         on the **customer dashboard** at `/account`, NOT the
         storefront home. Bug ref: AI Agent 2026-05-14 — "dashboard
         not found after login". */
      const target =
        forceRedirect ?? (isPanelRole(role) ? '/panel/dashboard' : '/account');
      navigate(target);
    } catch (err) {
      const apiMsg =
        (err as { response?: { data?: { message?: string; data?: unknown } } })
          ?.response?.data?.message ?? null;
      // Validation errors come back as `{ data: { FieldName: [errors] } }`.
      const validationData = (err as { response?: { data?: { data?: unknown } } })
        ?.response?.data?.data;
      let validationMsg: string | null = null;
      if (validationData && typeof validationData === 'object') {
        const firstField = Object.entries(validationData as Record<string, unknown>)[0];
        if (firstField) {
          const [field, messages] = firstField;
          const first = Array.isArray(messages) ? String(messages[0]) : String(messages);
          validationMsg = `${field}: ${first}`;
        }
      }
      setError(
        validationMsg ?? apiMsg ?? (err as Error)?.message ?? 'Registration failed',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Create your KSS account"
      width={520}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {onSwitchToLogin ? (
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
                onSwitchToLogin();
              }}
              disabled={submitting}
              className="text-xs text-content-secondary hover:text-content-primary disabled:opacity-50"
            >
              Already have an account? <span className="font-semibold text-primary">Sign in</span>
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create account'}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="grid gap-3">
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Company textbox is hidden by default — the tenant code is
            auto-derived from the hostname (subdomain). Flip the
            `VITE_SHOW_COMPANY_NAME=Y` env flag to expose this field
            for white-label / reseller deployments. */}
        {showCompanyInput && (
          <Input
            label="Company"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="sso"
            autoComplete="organization"
            hint="Pre-filled from your subdomain — change to register on a different tenant."
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            placeholder="Rajesh"
          />
          <Input
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            placeholder="Mehta"
          />
        </div>

        <Input
          label="Username"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          autoComplete="username"
          placeholder="rajesh.mehta"
        />

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="rajesh@example.com"
        />

        <Input
          label="Phone (optional)"
          value={phone}
          /* Numeric-only sanitiser — Bug ref: AI Agent Issue #2. */
          onChange={(e) => setPhone(e.target.value.replace(/\D+/g, '').slice(0, 15))}
          autoComplete="tel"
          inputMode="numeric"
          pattern="\d{10,15}"
          maxLength={15}
          placeholder="9876543210"
          hint="10-digit Indian mobile number."
        />

        <div className="grid grid-cols-2 gap-3">
          <PasswordField
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="••••••"
          />
          <PasswordField
            label="Confirm password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="••••••"
          />
        </div>

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
